import { z } from 'zod';
import { SystemDisplaySchema } from '../../entities/system/system.schema';
import { PopulatedDepartmentSchema } from '../../entities/department/department.schema';
import { PopulatedRoleSchema } from '../../entities/role/role.schema';
import { AvatarSchema } from '../../shared/avatar.schema';
import { SystemCapabilitySchema } from '../../shared/authorization';
import { CapacityBindingSchema } from '../../shared/capacity';
import { StepActionTypeSchema } from '../../shared/step-action-type.schema';
import { IncludeDeletedRequestSchema } from '../../shared/include-deleted.schema';
import { SystemLogoDataSchema } from '../../shared/logo.schema';
/**
 * Show System Request Schema
 * GET /api/v2/systems/:id
 */ export const ShowSystemRequestSchema = IncludeDeletedRequestSchema;
/**
 * Impacted Step Schema
 * Represents a process step that involves this system
 */ export const ImpactedStepSchema = z.object({
    _id: z.string(),
    stepNumber: z.number(),
    name: z.string(),
    generatedDescription: z.string().nullable().optional(),
    actionType: StepActionTypeSchema,
    department: PopulatedDepartmentSchema.nullable(),
    rating: z.number().nullable(),
    hasMediaBreak: z.boolean(),
    hasIntraStepMediaBreak: z.boolean()
});
/**
 * Impacted Process Schema
 * Represents a process that has steps involving this system
 */ export const ImpactedProcessSchema = z.object({
    _id: z.string(),
    name: z.string(),
    department: PopulatedDepartmentSchema.nullable(),
    stepsInvolved: z.number(),
    averageRating: z.number().nullable(),
    steps: z.array(ImpactedStepSchema),
    capacityBinding: CapacityBindingSchema.nullable(),
    mediaBreakCount: z.number(),
    intraStepMediaBreakCount: z.number()
});
/**
 * System User Involvement Type
 * Represents how a user is involved with a system through process steps
 */ export const SystemUserInvolvementTypeSchema = z.enum([
    'owner',
    'helper',
    'whoElseDoesThis'
]);
/**
 * System User Process Involvement Schema
 * Represents a user's involvement with a system in a specific process
 */ export const SystemUserProcessInvolvementSchema = z.object({
    processId: z.string(),
    processName: z.string(),
    department: PopulatedDepartmentSchema.nullable(),
    involvementTypes: z.array(SystemUserInvolvementTypeSchema),
    stepCount: z.number()
});
/**
 * System User Schema
 * Represents a user who interacts with a system through process steps
 */ export const SystemUserSchema = z.object({
    _id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    avatar: AvatarSchema.optional(),
    department: PopulatedDepartmentSchema.nullable(),
    role: PopulatedRoleSchema.nullable(),
    processInvolvements: z.array(SystemUserProcessInvolvementSchema),
    totalProcesses: z.number(),
    capacityBinding: CapacityBindingSchema.nullable()
});
/**
 * System Role Stat Schema
 * Represents a role with its user count within this system's context
 */ export const SystemRoleStatSchema = z.object({
    _id: z.string(),
    name: z.string(),
    userCount: z.number()
});
/**
 * System Department Stat Schema
 * Represents a department with its user count within this system's context
 */ export const SystemDepartmentStatSchema = z.object({
    _id: z.string(),
    name: z.string(),
    abbreviation: z.string(),
    userCount: z.number()
});
/**
 * System OrgFunction Stat Schema
 * Represents a OrgFunction (team/role) with the count of this system's involved
 * users who hold it, out of its total derived membership.
 */ export const SystemOrgFunctionStatSchema = z.object({
    _id: z.string(),
    name: z.string(),
    userCount: z.number(),
    totalMembers: z.number(),
    percentage: z.number()
});
/**
 * Critical Dependency User Schema
 * User assigned to a critical dependency step
 */ export const CriticalDependencyUserSchema = z.object({
    _id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    avatar: AvatarSchema.optional()
});
/**
 * Critical Dependency Schema
 * Represents a step with "bus factor" risk:
 * - System is used as helper
 * - Assigned to a single User
 * - No accepted whoElseDoesThis entries (no backup)
 */ export const CriticalDependencySchema = z.object({
    _id: z.string(),
    stepName: z.string(),
    process: z.object({
        _id: z.string(),
        name: z.string()
    }),
    assignedTo: CriticalDependencyUserSchema,
    role: PopulatedRoleSchema.nullable(),
    capacityBinding: CapacityBindingSchema.nullable()
});
/**
 * System Interaction Schema
 * Represents a connected system (upstream or downstream) with occurrence count
 */ export const SystemInteractionSchema = z.object({
    _id: z.string(),
    name: z.string(),
    logo: SystemLogoDataSchema.optional(),
    count: z.number()
});
/**
 * System Interactions Schema
 * Contains arrays of upstream (input) and downstream (output) systems
 */ export const SystemInteractionsSchema = z.object({
    upstream: z.array(SystemInteractionSchema),
    downstream: z.array(SystemInteractionSchema)
});
/**
 * Media Break System Schema
 * Represents a system involved in a media break transition
 */ export const MediaBreakSystemSchema = z.object({
    _id: z.string(),
    name: z.string(),
    logo: SystemLogoDataSchema.optional()
});
/**
 * Media Break Transition Schema
 * Represents a system-to-system transition that causes a media break
 */ export const MediaBreakTransitionSchema = z.object({
    fromSystem: MediaBreakSystemSchema,
    toSystem: MediaBreakSystemSchema,
    processCount: z.number()
});
/**
 * Media Break Map Schema
 * Analysis of media breaks (Systembruch) for a system
 */ export const MediaBreakMapSchema = z.object({
    breaksBefore: z.number(),
    breaksAfter: z.number(),
    mostFrequentBreaks: z.array(MediaBreakTransitionSchema)
});
/**
 * System Statistics Schema
 * Aggregated statistics for a system based on ProcessStepRecording involvement
 */ export const SystemStatisticsSchema = z.object({
    totalUsers: z.number(),
    totalOrgFunctions: z.number(),
    totalRoles: z.number(),
    totalProcesses: z.number(),
    averageStepRating: z.number().nullable(),
    capacityBinding: CapacityBindingSchema.nullable(),
    impactedProcesses: z.array(ImpactedProcessSchema).nullable(),
    systemUsers: z.array(SystemUserSchema).nullable(),
    topRoles: z.array(SystemRoleStatSchema),
    topDepartments: z.array(SystemDepartmentStatSchema),
    topOrgFunctions: z.array(SystemOrgFunctionStatSchema),
    allRoles: z.array(SystemRoleStatSchema).optional(),
    allDepartments: z.array(SystemDepartmentStatSchema).optional(),
    allOrgFunctions: z.array(SystemOrgFunctionStatSchema).optional(),
    criticalDependencies: z.array(CriticalDependencySchema).nullable(),
    systemInteractions: SystemInteractionsSchema.optional(),
    mediaBreakMap: MediaBreakMapSchema.optional()
});
/**
 * Show System Response Schema
 * GET /api/v2/systems/:id
 * Uses SystemDisplaySchema which requires populated systemOwner
 */ export const ShowSystemResponseSchema = z.object({
    data: SystemDisplaySchema.extend({
        statistics: SystemStatisticsSchema.optional()
    }),
    capabilities: SystemCapabilitySchema,
    issues: z.array(z.any()).optional()
});

//# sourceMappingURL=show-system.schema.js.map